import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

const SECTIONS = [
  'dashboard',
  'seo-optimizer',
  'seo-bulk',
  'seo-keywords',
  'alt-text-products',
  'image-optimizer',
  'main-image-picker',
  'bulk-product-editor',
  'pricing-new',
  'help',
];

export default function HelpCopilotPanel({ isOpen, onClose, onGoTo, onStartGuidedTour }) {
  const { t, i18n } = useTranslation();

  const items = useMemo(
    () =>
      SECTIONS.map((section) => ({
        section,
        title: t(`mainApp.map.${section}.title`),
        description: t(`mainApp.map.${section}.description`),
      })),
    [t, i18n.language]
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div
        role="presentation"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.35)',
          zIndex: 10490,
        }}
        onClick={onClose}
      />
      <div
        className="shadow-lg"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: 'min(420px, 100vw)',
          background: '#fff',
          zIndex: 10500,
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '-8px 0 32px rgba(15, 23, 42, 0.12)',
        }}
      >
        <div className="d-flex align-items-center justify-content-between border-bottom p-3">
          <div>
            <div className="fw-bold" style={{ fontSize: '1.1rem', color: '#111827' }}>
              {t('mainApp.appMapTitle')}
            </div>
            <div className="small text-muted">{t('mainApp.appMapSubtitle')}</div>
          </div>
          <button type="button" className="btn btn-link p-1 text-muted" onClick={onClose} aria-label={t('mainApp.closePanel')}>
            <X size={22} />
          </button>
        </div>
        <div className="flex-grow-1 overflow-auto p-3">
          <div className="d-flex flex-column gap-2">
            {items.map((it) => (
              <button
                key={it.section}
                type="button"
                className="btn btn-outline-secondary text-start py-3"
                style={{ borderRadius: 10, whiteSpace: 'normal' }}
                onClick={() => onGoTo(it.section)}
              >
                <div className="fw-semibold" style={{ color: '#111827' }}>
                  {it.title}
                </div>
                <div className="small text-muted mt-1" style={{ lineHeight: 1.45 }}>
                  {it.description}
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className="border-top p-3 bg-light">
          <button type="button" className="btn btn-primary w-100 fw-semibold" onClick={() => onStartGuidedTour()}>
            {t('mainApp.startGuidedTour')}
          </button>
        </div>
      </div>
    </>
  );
}
